import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import {
  X,
  UploadCloud,
  FileSpreadsheet,
  DownloadCloud,
  Loader2,
  AlertCircle,
  ListX,
  GraduationCap,
  BookOpen,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { importLessonsFromExcel } from "../../../api/elearning/lessons/lessons";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import { getAllCoursesNoPaging } from "../../../api/elearning/courses/courses";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { downloadLessonImportTemplate } from "./lessonImportTemplateXlsx";

const modalSelectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...provided,
    backgroundColor: state.isDisabled ? "#fafafa" : "#ffffff",
    borderColor: state.isFocused ? "#f87171" : "#e2e8f0",
    borderWidth: "1px",
    borderRadius: "0.75rem",
    minHeight: 44,
    height: 44,
    boxShadow: "none",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": {
      borderColor: state.isFocused ? "#f87171" : "#cbd5e1",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: 42,
    padding: "0 12px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: 42,
  }),
  placeholder: (base) => ({
    ...base,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#94a3b8",
    fontSize: "0.875rem",
  }),
  singleValue: (base) => ({
    ...base,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "0.875rem",
    fontWeight: 500,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

/**
 * Nhập danh sách bài học (chỉ metadata) cho một khóa học.
 * Hỗ trợ chọn Môn học và Khóa học trực tiếp trong modal.
 */
const LessonImportModal = ({
  isOpen,
  onClose,
  onSuccess,
  courseId: propCourseId,
  courseName: propCourseName,
  initialSubjectId = "",
  initialCourseId = "",
  lockCourse = false,
}) => {
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const isFixedCourse = Boolean(lockCourse && propCourseId);

  // Khởi tạo và tải danh sách môn học khi mở modal
  useEffect(() => {
    if (!isOpen) return;

    const initSub = initialSubjectId || "";
    const initCourse = initialCourseId || propCourseId || "";
    setSelectedSubjectId(initSub);
    setSelectedCourseId(initCourse);
    setFile(null);
    setImportReport(null);
    setLoading(false);

    if (isFixedCourse) return;

    let cancelled = false;
    setLoadingSubjects(true);
    getSubjectsLookup({ is_active: "true" })
      .then((data) => {
        if (!cancelled) {
          setSubjects(Array.isArray(data) ? data : []);
        }
      })
      .catch((err) => {
        console.error("Lỗi tải môn học:", err);
        if (!cancelled) setSubjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSubjects(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, initialSubjectId, initialCourseId, propCourseId, isFixedCourse]);

  // Tải danh sách khóa học khi môn học thay đổi
  useEffect(() => {
    if (!isOpen || isFixedCourse || !selectedSubjectId) {
      setCourses([]);
      return;
    }

    let cancelled = false;
    setLoadingCourses(true);
    getAllCoursesNoPaging({ subject_id: selectedSubjectId, is_active: "true" })
      .then((data) => {
        if (!cancelled) {
          const list = Array.isArray(data) ? data : [];
          setCourses(list);
          setSelectedCourseId((prev) => {
            if (prev && list.some((c) => String(c.id) === String(prev))) {
              return prev;
            }
            return "";
          });
        }
      })
      .catch((err) => {
        console.error("Lỗi tải khóa học:", err);
        if (!cancelled) setCourses([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCourses(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, isFixedCourse, selectedSubjectId]);

  const subjectOptions = useMemo(() => {
    return (subjects || []).map((s) => ({
      value: s.id,
      label: s.name,
    }));
  }, [subjects]);

  const courseOptions = useMemo(() => {
    return (courses || []).map((c) => ({
      value: c.id,
      label: c.name,
    }));
  }, [courses]);

  const effectiveCourseId = isFixedCourse ? propCourseId : selectedCourseId;

  const currentSubject = subjectOptions.find((o) => o.value === selectedSubjectId);
  const currentCourse = courseOptions.find((o) => o.value === effectiveCourseId);
  const subjectLabel = currentSubject?.label || "";
  const courseLabel = isFixedCourse ? (propCourseName || "") : (currentCourse?.label || "");

  const handleDownloadTemplate = () => {
    if (!isFixedCourse && (!selectedSubjectId || !effectiveCourseId)) {
      showErrorToast("Vui lòng chọn Môn học và Khóa học trước khi tải file mẫu.");
      return;
    }
    downloadLessonImportTemplate({
      subjectName: subjectLabel,
      courseName: courseLabel,
    }).catch(() => {
      showErrorToast("Không tạo được file mẫu Excel.");
    });
  };

  const reset = () => {
    setFile(null);
    setLoading(false);
    setImportReport(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose?.();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const name = selected.name.toLowerCase();
    const ok =
      name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv");
    if (!ok) {
      showErrorToast("Vui lòng chọn file Excel (.xlsx, .xls) hoặc .csv");
      return;
    }
    setFile(selected);
    setImportReport(null);
  };

  const handleSubmit = async () => {
    if (!effectiveCourseId) {
      showErrorToast("Vui lòng chọn môn học và khóa học để nhập dữ liệu");
      return;
    }
    if (!file) {
      showErrorToast("Vui lòng chọn file để nhập");
      return;
    }
    setImportReport(null);
    setLoading(true);
    try {
      const data = await importLessonsFromExcel(file, effectiveCourseId);
      const imported = Number(data?.imported) || 0;
      const errors = Array.isArray(data?.errors) ? data.errors : [];
      const summaryMsg = typeof data?.message === "string" ? data.message : "";

      setImportReport({ imported, errors, message: summaryMsg });

      const hasErrors = errors.length > 0;
      const fullSuccess = imported > 0 && !hasErrors;

      if (imported > 0) {
        onSuccess?.(effectiveCourseId, selectedSubjectId);
      }

      if (fullSuccess) {
        showSuccessToast(summaryMsg || `Đã nhập ${imported} bài học.`);
        setLoading(false);
        reset();
        onClose?.();
        return;
      }

      if (imported > 0 && hasErrors) {
        showSuccessToast(
          `Đã nhập ${imported} bài học. Còn ${errors.length} dòng lỗi — xem chi tiết bên dưới.`
        );
      } else if (hasErrors) {
        showErrorToast(
          summaryMsg ||
            `Không nhập được dòng nào. Có ${errors.length} lỗi — xem chi tiết bên dưới.`
        );
      } else if (summaryMsg) {
        showSuccessToast(summaryMsg);
      }
    } catch (err) {
      const apiMsg = err.response?.data?.message || err.response?.data?.error;
      const text =
        apiMsg || "Không thể nhập file. Kiểm tra định dạng và dữ liệu.";
      showErrorToast(text);
      setImportReport({
        imported: 0,
        errors: [{ line: "—", message: text }],
        message: "",
      });
    } finally {
      setLoading(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            className="relative w-full max-w-xl max-h-[90vh] flex flex-col bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden text-left"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex justify-between items-start bg-gray-50/80">
              <div>
                <h2 className="text-xl font-black text-gray-800 flex items-center gap-2 uppercase tracking-tight">
                  <UploadCloud className="text-red-600 shrink-0" size={24} />
                  Nhập danh sách bài học
                </h2>
                {isFixedCourse ? (
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Khóa học:{" "}
                    <span className="font-bold text-gray-700">
                      {propCourseName || "—"}
                    </span>
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Chọn môn học và khóa học để nhập danh sách bài học từ Excel.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-200 text-gray-400 transition-all disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
              {/* Form chọn Môn học & Khóa học */}
              {!isFixedCourse ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-gray-700">
                      <GraduationCap size={15} className="text-red-500" />
                      Môn học <span className="text-red-500">*</span>
                    </label>
                    <Select
                      styles={modalSelectStyles}
                      options={subjectOptions}
                      value={
                        subjectOptions.find(
                          (o) => o.value === selectedSubjectId
                        ) || null
                      }
                      onChange={(opt) => {
                        setSelectedSubjectId(opt?.value || "");
                        setSelectedCourseId("");
                      }}
                      placeholder={
                        loadingSubjects ? "Đang tải…" : "Chọn môn học…"
                      }
                      isLoading={loadingSubjects}
                      isClearable
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-gray-700">
                      <BookOpen size={15} className="text-red-500" />
                      Khóa học <span className="text-red-500">*</span>
                    </label>
                    <Select
                      styles={modalSelectStyles}
                      options={courseOptions}
                      value={
                        courseOptions.find(
                          (o) => o.value === selectedCourseId
                        ) || null
                      }
                      onChange={(opt) =>
                        setSelectedCourseId(opt?.value || "")
                      }
                      placeholder={
                        !selectedSubjectId
                          ? "Chọn môn học trước…"
                          : loadingCourses
                            ? "Đang tải…"
                            : courseOptions.length === 0
                              ? "Không có khóa học"
                              : "Chọn khóa học…"
                      }
                      isLoading={loadingCourses}
                      isDisabled={!selectedSubjectId || loadingCourses}
                      isClearable
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                    />
                  </div>
                </div>
              ) : null}

              {/* Upload file */}
              <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 hover:border-red-300 hover:bg-red-50/20 transition-all relative text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <div className="w-14 h-14 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <FileSpreadsheet size={28} />
                </div>
                <p className="text-sm font-bold text-gray-800">
                  {file ? file.name : "Chọn hoặc kéo thả file vào đây"}
                </p>
                <p className="text-[11px] text-gray-400 mt-1 font-medium">
                  Khuyến nghị .xlsx (file mẫu). Vẫn hỗ trợ .xls, .csv
                </p>
              </div>

              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-100 bg-red-50/50 text-red-700 text-xs font-bold uppercase tracking-wide hover:bg-red-50 transition-all"
              >
                <DownloadCloud size={16} />
                Tải file mẫu Excel (.xlsx)
              </button>

              <div className="flex gap-3 p-4 bg-amber-50 border border-amber-100 rounded-2xl">
                <AlertCircle className="text-amber-600 shrink-0" size={18} />
                <p className="text-[11px] text-amber-900 leading-relaxed font-medium">
                  File mẫu có sheet <span className="font-bold">Hướng dẫn</span>{" "}
                  (chi tiết cột / quy trình) và sheet{" "}
                  <span className="font-bold">Danh sách</span> dạng bảng Excel —
                  nhập thứ tự, tên, mô tả. Tài liệu / file đính kèm bổ sung sau
                  tại <span className="font-bold">Sửa bài học</span>. Bài trùng
                  tên trong cùng khóa học sẽ bị bỏ qua.
                </p>
              </div>

              {importReport && importReport.errors.length > 0 && (
                <div className="rounded-2xl border border-red-200 bg-red-50/60 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-red-100 bg-red-50">
                    <ListX className="text-red-600 shrink-0" size={18} />
                    <div>
                      <p className="text-xs font-black text-red-800 uppercase tracking-tight">
                        Chi tiết lỗi ({importReport.errors.length} dòng)
                      </p>
                      {importReport.imported > 0 && (
                        <p className="text-[11px] text-red-700 font-medium mt-0.5">
                          Đã nhập thành công {importReport.imported} bài học; các
                          dòng sau bị bỏ qua.
                        </p>
                      )}
                    </div>
                  </div>
                  <ul className="max-h-52 overflow-y-auto divide-y divide-red-100/80 text-left">
                    {importReport.errors.map((row, idx) => (
                      <li
                        key={`${row.line}-${idx}`}
                        className="px-4 py-2.5 text-[12px] text-red-900 leading-snug"
                      >
                        <span className="font-bold text-red-800 tabular-nums">
                          Dòng {row.line}
                        </span>
                        <span className="text-red-600 mx-1.5">·</span>
                        <span className="text-red-900/90">{row.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-200 transition-all disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !file || !effectiveCourseId}
                className="px-6 py-2.5 rounded-xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-100 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                {loading ? "Đang xử lý…" : "Nhập dữ liệu"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default LessonImportModal;
